import { TICK_HZ } from '@somnhold/content';
import { reportError } from '../errors.ts';

/** `?smooth=0`: draw on the 12 Hz beat as before the glide, to compare side by side (also live). */
const STEPPED = new URLSearchParams(location.search).get('smooth') === '0';
/**
 * `?fps=60`: never draw faster than that, to try a 120 Hz phone at 60 side by side. Also from localStorage
 * `somnhold.fps`, which the e2e tests set so dozens of screens drawing at once leave the CPU to the tests.
 */
const MAX_FPS = Number(new URLSearchParams(location.search).get('fps')) || storedFps();

function storedFps(): number {
  try {
    return Number(localStorage.getItem('somnhold.fps')) || 0;
  } catch {
    return 0;
  }
}
/**
 * Drawing a frame takes longer than this (ms, on average) and the screen drops to HEAVY_FPS until it eases
 * off below CALM_MS for CALM_FOR ms: a slow phone with a crowd on screen spent its whole CPU drawing at 60
 * and had nothing left (heat, battery, a stall at the next spike). The simulation is unaffected.
 */
const HEAVY_MS = 10;
const CALM_MS = 5;
const CALM_FOR = 3000;
const HEAVY_FPS = 30;

/**
 * Fixed 12 Hz simulation clock with a full-rate render callback (SPEC §15: stop-motion entities,
 * smooth camera/UI). Catches up at most a few ticks per frame so a background tab doesn't spiral.
 */
export class Loop {
  private raf = 0;
  private last = 0;
  private acc = 0;
  speed = 1;
  paused = false;
  /** A solo clock waiting to start (the fight's models still baking): frames draw, no tick runs. */
  held = false;
  running = false;
  /** Rolling FPS estimate. */
  fps = 60;
  /** Rolling cost of a frame's work in ms, and the frame rate cap it set (0: none). */
  private work = 0;
  private calmSince = 0;
  /** What the last frame's work cost, in ms (PerfMeter reads it after the drawing). */
  lastMs = 0;
  cap = 0;

  /** How far into the next tick this frame is (0..2, past 1 while a live tick is late): how far what moves has glided. */
  get alpha(): number {
    return this.paused || STEPPED ? 2 : Math.min(2, this.acc * TICK_HZ);
  }

  /** Live play: the ticks come from the room, not from this clock. */
  driven = false;

  /** A tick came from elsewhere (live play): what moves glides towards it from now. */
  beat(): void {
    this.acc = 0;
  }

  constructor(
    private onTick: () => void,
    private onFrame: (dt: number) => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const frame = (now: number): void => {
      if (!this.running) return;
      // The next frame first: an error below must never stop the loop (the screen froze for good).
      this.raf = requestAnimationFrame(frame);
      // A little early still counts (vsync jitter), so a 60 Hz screen keeps every frame under `?fps=60`.
      const limit = MAX_FPS || this.cap;
      if (limit && now - this.last < 1000 / limit - 2) return;
      const t0 = performance.now();
      try {
        this.advance(now);
      } catch (e) {
        reportError(e, 'frame');
      }
      this.lastMs = performance.now() - t0;
      if (!MAX_FPS) this.pace(this.lastMs, now);
    };
    this.raf = requestAnimationFrame(frame);
  }

  /** Heavy frames cap the rate (HEAVY_FPS); light ones for a while lift the cap. */
  private pace(ms: number, now: number): void {
    this.work = this.work * 0.9 + ms * 0.1;
    if (!this.cap) {
      if (this.work > HEAVY_MS) {
        this.cap = HEAVY_FPS;
        this.calmSince = 0;
      }
    } else if (this.work < CALM_MS) {
      this.calmSince ||= now;
      if (now - this.calmSince > CALM_FOR) this.cap = 0;
    } else this.calmSince = 0;
  }

  /** One frame: how far the clock moved, the ticks it owes (solo), then the drawing. */
  private advance(now: number): void {
    const dt = Math.min(0.25, (now - this.last) / 1000);
    this.last = now;
    if (dt > 0) this.fps = this.fps * 0.95 + (1 / dt) * 0.05;
    if (!this.paused && this.driven) {
      // Live: the ticks come from the room (beat()); the clock only measures how far to glide.
      this.acc = Math.min(2 / TICK_HZ, this.acc + dt);
    } else if (!this.paused && !this.held) {
      this.acc += dt * this.speed;
      let n = 0;
      const step = 1 / TICK_HZ;
      // The catch-up cap grows with the speed, so a fast-forward keeps its pace on a slow frame rate.
      const cap = 8 * Math.max(1, Math.ceil(this.speed));
      while (this.acc >= step && n < cap) {
        this.onTick();
        this.acc -= step;
        n++;
      }
      if (n === cap) this.acc = 0;
    }
    this.onFrame(dt);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }
}
